import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useEsMovil } from '../../indicadores/hooks/useEsMovil';
import { MetasApiError, MetasColaboradorService } from '../services/metasColaborador.service';
import {
  TIPOS_PENDIENTE,
  type AreaPendiente,
  type ListaPendientesCatalogo,
  type MiAlcance,
  type PendienteCatalogo,
  type TiendaRef,
  type TipoPendiente,
} from '../types/metas.types';
import { guardarArchivo } from '../utils/descarga';
import { veTodasLasTiendas } from '../utils/direccion';
import { nombrePropio } from '../utils/formato';
import {
  esDeTienda,
  ETIQUETA_ACCION,
  ETIQUETA_TIPO_PENDIENTE,
  nombreTiendaPendiente,
  textoAbierto,
  textoCierre,
  textoFicha,
  textoMuestraAfectados,
  textoUltimaSincronizacion,
  tiendasDestino,
} from '../utils/pendientes';
import { CLASE_BOTON, CLASE_BOTON_TEXTO, CLASE_PANEL, CLASES_CAMPO, cx } from './ui/clases';

type AvisoToast = (severidad: 'success' | 'warn' | 'error', resumen: string, detalle?: string) => void;

interface Props {
  alcance: MiAlcance;
  onAviso: AvisoToast;
  /**
   * La pestaña está a la vista. Hasta que se abra por primera vez no se pide
   * nada; al volver a ella se relee (la sincronización corre cada hora).
   */
  activa?: boolean;
  /** «Ver tienda»: la pantalla abre la tienda (Captura, o Autorización si la cuenta no captura) en esa semana. */
  onVerTienda: (t: TiendaRef, semana: string | null) => void;
  /** Se sincronizó desde aquí: la pantalla relee el contador de la pestaña. */
  onCambio?: () => void;
  /**
   * «Ver en Pendientes de catálogo» desde el banner de una tienda: la lista
   * abre filtrada a ESA tienda. Cambia en cada pedido (`n`), así que pedir dos
   * veces la misma también cuenta.
   */
  filtroTienda?: (TiendaRef & { n: number }) | null;
  /** Espera de la búsqueda antes de pedirla (ms). Las pruebas la bajan. */
  demoraBusquedaMs?: number;
}

export const TITULO_PENDIENTES = 'Datos de catálogo que bloquean la captura o las comisiones';
export const DESCRIPCION_PENDIENTES =
  'Se corrigen en el sistema de origen; la lista se actualiza sola en la siguiente sincronización. Mientras un pendiente que bloquea siga abierto, la tienda afectada no puede enviarse ni autorizarse.';

const AREAS: AreaPendiente[] = ['Recursos Humanos', 'Comercial'];

/** Mientras una corrida sigue en curso, cada cuánto se vuelve a mirar (ms). */
const REVISAR_EN_CURSO_MS = 10_000;

const IconoExcel = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="text-metas-exito">
    <path d="M14 3H6v18h12V7z" />
    <path d="M14 3v4h4M9.5 12l5 5M14.5 12l-5 5" />
  </svg>
);

const IconoActualizar: React.FC<{ girando?: boolean }> = ({ girando }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className={girando ? 'animate-spin' : undefined}>
    <path d="M20 11a8 8 0 0 0-14.9-4M4 4v4h4M4 13a8 8 0 0 0 14.9 4M20 20v-4h-4" />
  </svg>
);

const IconoBuscar = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="shrink-0 text-metas-texto-2">
    <circle cx="11" cy="11" r="7" />
    <path d="M20 20l-4-4" />
  </svg>
);

/** Candado: «Bloquea el envío» (con texto: el color nunca va solo). */
export const IconoBloquea = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="shrink-0">
    <rect x="5" y="11" width="14" height="10" rx="2" />
    <path d="M8 11V7a4 4 0 0 1 8 0v4" />
  </svg>
);

const IconoReloj = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="shrink-0">
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 2" />
  </svg>
);

/** «Bloquea el envío»: candado + texto, en rojo. */
export const MarcaBloquea: React.FC<{ texto?: string; className?: string }> = ({ texto = 'Bloquea el envío', className }) => (
  <span className={cx('inline-flex items-center gap-1 text-xs font-semibold text-metas-error', className)} data-testid="marca-bloquea">
    <IconoBloquea />
    {texto}
  </span>
);

/** La etiqueta ámbar del tipo (`.tag` del diseño), que parte en dos renglones si hace falta. */
const TagTipo: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="inline-flex items-center rounded-md bg-metas-aviso-fondo px-2 py-1 text-[11px] font-semibold leading-[1.3] text-metas-aviso">
    {children}
  </span>
);

/**
 * «Pendientes de catálogo» (fase 4, artboard `Pendientes`): datos que faltan o
 * están mal en su sistema de ORIGEN —BUK, el ERP, la fuente de metas de
 * tienda— y que bloquean la captura o las comisiones.
 *
 * Es de SÓLO LECTURA: aquí no se corrige nada. La lista es la de la última
 * sincronización (cada hora, y a mano con «Actualizar» para admin y regional);
 * un pendiente se cierra solo cuando la sincronización ya no lo detecta. Qué
 * tipos BLOQUEAN el envío y la autorización lo dice la API (configurable).
 *
 * - Las tarjetas por tipo son el filtro (una activa, `aria-pressed`); su número
 *   no depende del tipo elegido.
 * - «Ver tienda» abre la tienda en la Captura (o en Autorización si la cuenta no
 *   captura); «Ver afectados» filtra la lista a ese puesto y enseña a quiénes
 *   afecta; «Ver ficha» copia el número de empleado: no hay ficha de BUK a la
 *   que se pueda navegar desde aquí.
 */
export const PendientesTab: React.FC<Props> = ({
  alcance,
  onAviso,
  activa = true,
  onVerTienda,
  onCambio,
  filtroTienda = null,
  demoraBusquedaMs = 300,
}) => {
  const esMovil = useEsMovil(767);
  const [abierta, setAbierta] = useState(!!activa);
  useEffect(() => {
    if (activa) setAbierta(true);
  }, [activa]);

  const [tipo, setTipo] = useState<TipoPendiente | null>(null);
  const [area, setArea] = useState<AreaPendiente | null>(null);
  const [busqueda, setBusqueda] = useState('');
  const [q, setQ] = useState('');
  const [resueltos, setResueltos] = useState(false);
  // «Ver afectados»: la lista queda en ESE pendiente y se enseñan sus afectados.
  const [afectadosDe, setAfectadosDe] = useState<number | null>(null);
  // Filtro por tienda (desde el banner de la Captura): se aplica aquí, sobre la
  // lista. Buscar «VAZ 1» en `q` también encontraba VAZ 114 y VAZ 12 (F4-V7).
  const [tiendaFiltro, setTiendaFiltro] = useState<TiendaRef | null>(filtroTienda ? { chain: filtroTienda.chain, num: filtroTienda.num } : null);
  useEffect(() => {
    if (!filtroTienda) return;
    setTiendaFiltro({ chain: filtroTienda.chain, num: filtroTienda.num });
    setTipo(null);
    setAfectadosDe(null);
  }, [filtroTienda]);
  const refAfectados = useRef<HTMLHeadingElement>(null);

  const [datos, setDatos] = useState<ListaPendientesCatalogo | null>(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sincronizando, setSincronizando] = useState(false);
  const [descargando, setDescargando] = useState(false);

  // La búsqueda se pide al dejar de teclear.
  useEffect(() => {
    const t = setTimeout(() => setQ(busqueda.trim()), demoraBusquedaMs);
    return () => clearTimeout(t);
  }, [busqueda, demoraBusquedaMs]);

  // Sólo cuenta la respuesta del ÚLTIMO pedido.
  const pedido = useRef(0);
  const filtros = { tipo, area, q, resueltos };
  const cargar = useCallback(async () => {
    const n = ++pedido.current;
    setCargando(true);
    setError(null);
    try {
      const d = await MetasColaboradorService.pendientesCatalogo({ tipo, area, q, resueltos });
      if (n !== pedido.current) return;
      setDatos(d);
    } catch (e) {
      if (n !== pedido.current) return;
      setError((e as Error).message);
    } finally {
      if (n === pedido.current) setCargando(false);
    }
  }, [tipo, area, q, resueltos]);

  useEffect(() => {
    if (abierta) void cargar();
  }, [abierta, cargar]);

  // Al VOLVER a la pestaña, se relee: la sincronización corre cada hora.
  const cargarRef = useRef(cargar);
  cargarRef.current = cargar;
  const activaAntes = useRef(activa);
  useEffect(() => {
    const era = activaAntes.current;
    activaAntes.current = activa;
    if (activa && !era && abierta) void cargarRef.current();
  }, [activa, abierta]);

  // Con una corrida en curso (de otra persona o del cron) se vuelve a mirar sola.
  const enCurso = !!datos?.enCurso;
  useEffect(() => {
    if (!enCurso || !activa || sincronizando) return undefined;
    const t = setTimeout(() => void cargarRef.current(), REVISAR_EN_CURSO_MS);
    return () => clearTimeout(t);
  }, [enCurso, activa, sincronizando, datos]);

  const sincronizar = async () => {
    if (sincronizando) return;
    setSincronizando(true);
    try {
      const r = await MetasColaboradorService.sincronizarPendientes();
      if (r.error) onAviso('error', 'La sincronización falló', `${r.error}. La lista sigue siendo la de antes.`);
      else {
        const partes = [`${r.nuevos} nuevo(s)`, `${r.resueltos} resuelto(s)`, `${r.abiertos} abierto(s)`];
        const sinEvaluar = r.sinEvaluar.length
          ? ` No se pudo revisar: ${r.sinEvaluar.map((t) => ETIQUETA_TIPO_PENDIENTE[t] ?? t).join(', ')} (esos siguen como estaban).`
          : '';
        onAviso(r.sinEvaluar.length ? 'warn' : 'success', 'Pendientes actualizados', `${partes.join(' · ')}.${sinEvaluar}`);
      }
      onCambio?.();
    } catch (e) {
      if (e instanceof MetasApiError && e.status === 409)
        onAviso('warn', 'Ya se está actualizando', 'Otra sincronización está en curso: la lista se refresca al terminar.');
      else onAviso('error', 'No se pudo actualizar', (e as Error).message);
    } finally {
      setSincronizando(false);
      void cargarRef.current();
    }
  };

  const exportar = async () => {
    if (descargando) return;
    setDescargando(true);
    try {
      guardarArchivo(await MetasColaboradorService.pendientesExcel(filtros, alcance.hoy));
    } catch (e) {
      onAviso('error', 'No se pudo exportar', (e as Error).message);
    } finally {
      setDescargando(false);
    }
  };

  const verFicha = async (p: PendienteCatalogo) => {
    const texto = textoFicha(p);
    const que = p.numeroColaborador !== null ? `N° ${texto}` : texto;
    const donde = `No hay ficha a la que se pueda ir desde aquí: búscalo en BUK. Lo corrige ${p.area}.`;
    try {
      if (!navigator.clipboard?.writeText) throw new Error('sin portapapeles');
      await navigator.clipboard.writeText(texto);
      onAviso('success', `${que} copiado`, donde);
    } catch {
      onAviso('warn', `${nombrePropio(p.referencia) || p.referencia}: ${que}`, `No se pudo copiar al portapapeles. ${donde}`);
    }
  };

  const verAfectados = (p: PendienteCatalogo) => setAfectadosDe((x) => (x === p.id ? null : p.id));

  // Una tarjeta (tipo) es OTRO filtro: quita el de tienda y el de afectados.
  const elegirTipo = (t: TipoPendiente | null) => {
    setTipo(t);
    setAfectadosDe(null);
    setTiendaFiltro(null);
  };

  // ------------------------------------------------------------ lo que se ve
  const items = datos?.items ?? [];
  const deTienda = tiendaFiltro ? items.filter((i) => esDeTienda(i, tiendaFiltro)) : items;
  const visibles = afectadosDe !== null ? deTienda.filter((i) => i.id === afectadosDe) : deTienda;
  const afectado = afectadosDe !== null ? items.find((i) => i.id === afectadosDe) ?? null : null;
  // Con la lista filtrada a una tienda, las tarjetas cuentan los de ESA tienda.
  const conteoTipo = (t: TipoPendiente | null): number | undefined => {
    if (!datos) return undefined;
    if (!tiendaFiltro) return t === null ? datos.conteos.total : datos.conteos.porTipo[t];
    return deTienda.filter((i) => !i.resuelto && (t === null || i.tipo === t)).length;
  };

  // «Ver afectados»: el panel se abre ARRIBA de la lista; en el teléfono el
  // botón queda abajo y el encabezado del panel, fuera de la vista. Se lleva a
  // la vista y se le pasa el foco (F4-V5).
  const hayAfectado = !!afectado;
  useEffect(() => {
    if (afectadosDe === null || !hayAfectado) return;
    const h = refAfectados.current;
    if (!h) return;
    h.scrollIntoView?.({ block: 'start' });
    h.focus?.({ preventScroll: true });
  }, [afectadosDe, hayAfectado]);
  const etiquetaTipo = (t: TipoPendiente): string =>
    datos?.tipos.find((x) => x.tipo === t)?.etiqueta ?? ETIQUETA_TIPO_PENDIENTE[t];
  const bloqueaTipo = (t: TipoPendiente): boolean => !!datos?.tipos.find((x) => x.tipo === t)?.bloquea;
  const sync = datos ? textoUltimaSincronizacion(datos.ultimaSincronizacion, datos.enCurso) : null;
  const dias = datos?.diasAntiguedadRoja ?? 3;
  const puedeSincronizar = !!datos?.puedeSincronizar;

  const botonAccion = (p: PendienteCatalogo, ancho?: boolean) => {
    const clase = cx(CLASE_BOTON, '!h-9 !px-3 !text-[13px]', ancho && 'w-full max-md:!h-metas-tactil [@media(pointer:coarse)]:!h-metas-tactil');
    if (p.accion === 'ver_ficha')
      return (
        <button type="button" className={clase} onClick={() => void verFicha(p)} aria-label={`Ver ficha: ${p.referencia}`}>
          {ETIQUETA_ACCION.ver_ficha}
        </button>
      );
    if (p.accion === 'ver_afectados')
      return (
        <button
          type="button"
          className={clase}
          onClick={() => verAfectados(p)}
          aria-pressed={afectadosDe === p.id}
          aria-label={`Ver afectados: ${p.referencia}`}
        >
          {afectadosDe === p.id ? 'Ver todos' : ETIQUETA_ACCION.ver_afectados}
        </button>
      );
    const destinos = tiendasDestino(p, alcance);
    if (!destinos.length) return <span className="text-xs text-metas-texto-2">Fuera de tu alcance</span>;
    if (destinos.length === 1)
      return (
        <button
          type="button"
          className={clase}
          onClick={() => onVerTienda({ chain: destinos[0].chain, num: destinos[0].num }, p.semana)}
          aria-label={`Ver tienda ${nombreTiendaPendiente(destinos[0])}`}
        >
          {ETIQUETA_ACCION.ver_tienda}
        </button>
      );
    return (
      <div className={cx('flex flex-col items-end gap-1', ancho && 'w-full items-stretch')} role="group" aria-label={`Ver tienda: ${p.referencia}`}>
        {destinos.map((d) => (
          <button
            key={`${d.chain}|${d.num}`}
            type="button"
            className={clase}
            onClick={() => onVerTienda({ chain: d.chain, num: d.num }, p.semana)}
            aria-label={`Ver tienda ${nombreTiendaPendiente(d)}`}
          >
            Ver {d.chain} {d.num}
          </button>
        ))}
      </div>
    );
  };

  const celdaTipo = (p: PendienteCatalogo) => (
    <div className="flex min-w-0 flex-col items-start gap-1">
      <TagTipo>{p.tipoEtiqueta || etiquetaTipo(p.tipo)}</TagTipo>
      {p.resuelto ? (
        <span className={cx('text-xs font-semibold', textoCierre(p).vencio ? 'text-metas-texto-2' : 'text-metas-exito')} data-testid="cierre-pendiente">
          {textoCierre(p).texto}
        </span>
      ) : (
        p.bloquea && <MarcaBloquea />
      )}
    </div>
  );

  const abierto = (p: PendienteCatalogo) => {
    if (p.resuelto) return <span className="text-metas-texto-2">{textoAbierto(p.diasAbierto)}</span>;
    return p.antiguo ? (
      <span className="inline-flex items-center gap-1 font-semibold text-metas-error" title={`Abierto hace más de ${dias} días`}>
        <IconoReloj />
        {textoAbierto(p.diasAbierto)}
        <span className="sr-only"> (más de {dias} días)</span>
      </span>
    ) : (
      <span className="text-metas-texto-2">{textoAbierto(p.diasAbierto)}</span>
    );
  };

  const referencia = (p: PendienteCatalogo) => (
    <div className="flex min-w-0 flex-col gap-0.5">
      <span className="font-semibold [overflow-wrap:anywhere]">{p.referencia}</span>
      {p.referenciaSub && <span className="text-xs text-metas-texto-2 [overflow-wrap:anywhere]">{p.referenciaSub}</span>}
    </div>
  );

  // ------------------------------------------------------------ piezas
  const cabecera = (
    <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between md:gap-6">
      <div className="flex max-w-[760px] flex-col gap-1">
        <h2 className="m-0 text-lg font-semibold">{TITULO_PENDIENTES}</h2>
        <p className="m-0 text-sm leading-normal text-metas-texto-2">{DESCRIPCION_PENDIENTES}</p>
      </div>
      <div className="flex flex-wrap items-center gap-2 md:shrink-0 md:justify-end">
        {sync && (
          <span
            className={cx('text-xs', sync.fallo ? 'inline-flex items-center gap-1 font-semibold text-metas-error' : 'text-metas-texto-2')}
            data-testid="ultima-sincronizacion"
            role={sync.fallo ? 'alert' : undefined}
            title={sync.fallo ? datos?.ultimaSincronizacion?.error ?? undefined : undefined}
          >
            {sync.fallo && (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M12 3l10 18H2z" />
                <path d="M12 10v4M12 17.5h.01" />
              </svg>
            )}
            {sync.texto}
          </span>
        )}
        {puedeSincronizar && (
          <button
            type="button"
            className={CLASE_BOTON}
            onClick={() => void sincronizar()}
            disabled={sincronizando || enCurso}
            title={enCurso && !sincronizando ? 'Hay una sincronización en curso' : 'Revisar ahora BUK, el ERP y las metas de tienda'}
          >
            <IconoActualizar girando={sincronizando || enCurso} />
            {sincronizando || enCurso ? 'Actualizando…' : 'Actualizar'}
          </button>
        )}
        <button type="button" className={CLASE_BOTON} onClick={() => void exportar()} disabled={descargando || !datos}>
          <IconoExcel />
          Exportar Excel
        </button>
      </div>
    </div>
  );

  const tarjetas = (
    <div
      role="group"
      aria-label="Filtrar por tipo de pendiente"
      className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6"
      data-testid="tarjetas-tipo"
    >
      {[null, ...TIPOS_PENDIENTE].map((t) => {
        // Con la lista filtrada a un pendiente («Ver afectados») o a una tienda,
        // ninguna tarjeta está pulsada: «Todos» mentía.
        const on = afectadosDe === null && tiendaFiltro === null && tipo === t;
        const n = conteoTipo(t);
        const etiqueta = t === null ? 'Todos los pendientes' : etiquetaTipo(t);
        return (
          <button
            key={t ?? 'todos'}
            type="button"
            aria-pressed={on}
            onClick={() => elegirTipo(t)}
            data-tipo={t ?? 'todos'}
            className={cx(
              'metas-foco flex flex-col items-start gap-1 rounded-metas-panel border bg-metas-panel px-4 py-3.5 text-left text-metas-texto',
              on ? 'border-metas-primario shadow-metas-anillo-sel' : 'border-metas-borde hover:bg-metas-cabecera',
            )}
          >
            <span className="num text-2xl font-semibold">{n ?? '—'}</span>
            <span className="text-[13px] leading-[1.35] text-metas-texto-3">{etiqueta}</span>
            {t !== null && bloqueaTipo(t) && <MarcaBloquea className="!text-[11px]" />}
          </button>
        );
      })}
    </div>
  );

  const barra = (
    <div className="flex flex-col gap-3 border-b border-metas-borde-suave px-4 py-3 md:flex-row md:items-center">
      <div className={cx(CLASES_CAMPO.normal, 'md:w-[280px]')}>
        <IconoBuscar />
        <input
          type="search"
          aria-label="Buscar pendiente"
          placeholder="Buscar colaborador, N° o tienda"
          value={busqueda}
          onChange={(e) => {
            setBusqueda(e.target.value);
            setAfectadosDe(null);
          }}
          className="h-full w-full min-w-0 border-0 bg-transparent p-0 text-sm text-metas-texto outline-none"
        />
      </div>
      <div className={cx(CLASES_CAMPO.normal, 'relative md:w-[220px]')}>
        <select
          aria-label="Filtrar por área que resuelve"
          value={area ?? ''}
          onChange={(e) => {
            setArea((e.target.value || null) as AreaPendiente | null);
            setAfectadosDe(null);
          }}
          className="h-full w-full min-w-0 appearance-none border-0 bg-transparent p-0 pr-6 text-sm text-metas-texto outline-none"
        >
          <option value="">Resuelve: todas las áreas</option>
          {(datos?.areas?.length ? datos.areas : AREAS).map((a) => (
            <option key={a} value={a}>
              {a}
            </option>
          ))}
        </select>
        <i className="pi pi-chevron-down pointer-events-none absolute right-3 text-xs" aria-hidden="true" />
      </div>
      <div className="hidden flex-grow md:block" />
      <label className="flex items-center gap-2 text-[13px] text-metas-texto-3 max-md:min-h-metas-tactil [@media(pointer:coarse)]:min-h-metas-tactil">
        <input
          type="checkbox"
          className="h-[18px] w-[18px] accent-metas-primario"
          checked={resueltos}
          onChange={(e) => setResueltos(e.target.checked)}
        />
        Mostrar resueltos
      </label>
    </div>
  );

  // El administrador y dirección (21-oct-2026) ven a los afectados de todo el catálogo, no «de tus tiendas».
  const veTodas = veTodasLasTiendas(alcance);
  const panelAfectados = afectado && (
    <div className="flex flex-col gap-2 border-b border-metas-borde-suave bg-metas-fila-sel px-4 py-3 text-sm" data-testid="panel-afectados" role="region" aria-label={`Afectados: ${afectado.referencia}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3
          ref={refAfectados}
          tabIndex={-1}
          className="m-0 scroll-mt-4 text-sm font-semibold outline-none focus-visible:ring-2 focus-visible:ring-metas-primario"
          data-testid="encabezado-afectados"
        >
          Afectados por «{afectado.referencia}»
          {typeof afectado.datos?.afectados === 'number' ? ` · ${afectado.datos.afectados} en la cadena` : ''}
          {!veTodas && typeof afectado.datos?.personasTotal === 'number'
            ? ` · ${afectado.datos.personasTotal} en tus tiendas`
            : ''}
        </h3>
        <button type="button" className={CLASE_BOTON_TEXTO} onClick={() => setAfectadosDe(null)}>
          Quitar filtro
        </button>
      </div>
      {afectado.datos?.personas?.length ? (
        <ul className="m-0 grid list-none grid-cols-1 gap-1 p-0 md:grid-cols-2 xl:grid-cols-3">
          {afectado.datos.personas.map((x) => (
            <li key={`${x.num ?? x.nombre}|${x.chain}|${x.tienda}`} className="[overflow-wrap:anywhere]">
              <b className="font-semibold">{nombrePropio(x.nombre)}</b>
              <span className="text-metas-texto-2">
                {x.num !== null ? ` · N° ${x.num}` : ''} · {x.chain} {x.tienda}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <span className="text-metas-texto-2">{veTodas ? 'Sin personas en la lista.' : 'Ninguno en tus tiendas.'}</span>
      )}
      {textoMuestraAfectados(afectado.datos, veTodas) && (
        <span className="text-xs text-metas-texto-2" data-testid="muestra-afectados">
          {textoMuestraAfectados(afectado.datos, veTodas)}
        </span>
      )}
    </div>
  );

  const chipTienda = tiendaFiltro && (
    <div className="flex flex-wrap items-center gap-2 border-b border-metas-borde-suave bg-metas-fila-sel px-4 py-2.5 text-sm" data-testid="filtro-tienda">
      <span>
        Tienda: <b className="font-semibold">{tiendaFiltro.chain} {tiendaFiltro.num}</b>
        {(() => {
          const t = alcance.tiendas.find((x) => x.chain === tiendaFiltro.chain && x.num === tiendaFiltro.num);
          return t ? ` · ${t.tienda}` : '';
        })()}
      </span>
      <button type="button" className={CLASE_BOTON_TEXTO} onClick={() => setTiendaFiltro(null)}>
        Quitar
      </button>
    </div>
  );

  const vacio = datos && !visibles.length && (
    <p className="m-0 px-4 py-8 text-center text-sm text-metas-texto-2" data-testid="pendientes-vacio">
      {tipo || area || q || tiendaFiltro
        ? 'Ningún pendiente coincide con los filtros.'
        : resueltos
          ? 'No hay pendientes, ni abiertos ni resueltos.'
          : 'No hay pendientes abiertos: el catálogo está al día.'}
    </p>
  );

  const tabla = (
    // `relative`: los `sr-only` «(más de N días)» son absolutos y, sin él, se salían
    // del contenedor con scroll y la página se arrastraba de lado de 768 a ~1100 px
    // (865 px a 768; auditoría del 22-oct-2026).
    <div className="relative overflow-x-auto">
      <table className="w-full min-w-[1000px] table-fixed border-collapse text-sm" aria-label="Pendientes de catálogo">
        <colgroup>
          <col style={{ width: 200 }} />
          <col style={{ width: 170 }} />
          <col />
          <col style={{ width: '30%' }} />
          <col style={{ width: 150 }} />
          <col style={{ width: 96 }} />
          <col style={{ width: 140 }} />
        </colgroup>
        <thead>
          <tr className="bg-metas-cabecera">
            {['Tipo', 'Tienda', 'Colaborador o referencia', 'Qué bloquea', 'Resuelve', 'Abierto'].map((h) => (
              <th key={h} scope="col" className="px-3 py-3 text-left text-metas-th text-metas-texto-2">
                {h}
              </th>
            ))}
            <th scope="col" className="px-3 py-3 text-right text-metas-th text-metas-texto-2">
              Acción
            </th>
          </tr>
        </thead>
        <tbody>
          {visibles.map((p) => (
            <tr key={p.id} className={cx('border-t border-metas-borde-suave align-top', p.resuelto && 'text-metas-texto-2')} data-pendiente={p.id}>
              <td className="px-3 py-3">{celdaTipo(p)}</td>
              <td className="px-3 py-3 font-medium [overflow-wrap:anywhere]">{p.tiendaTexto}</td>
              <td className="px-3 py-3">{referencia(p)}</td>
              <td className="px-3 py-3 leading-[1.45] text-metas-texto-3 [overflow-wrap:anywhere]">{p.detalle}</td>
              <td className="px-3 py-3">{p.area}</td>
              <td className="num px-3 py-3">{abierto(p)}</td>
              <td className="px-3 py-3 text-right">{!p.resuelto && botonAccion(p)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const tarjetasMovil = (
    <ul className="m-0 flex list-none flex-col gap-2 p-3" aria-label="Pendientes de catálogo">
      {visibles.map((p) => (
        <li
          key={p.id}
          data-pendiente={p.id}
          className={cx(
            'flex flex-col gap-2 rounded-metas-panel border p-3.5',
            p.bloquea && !p.resuelto ? 'border-metas-tarjeta-pendiente-borde bg-metas-tarjeta-pendiente' : 'border-metas-borde bg-metas-panel',
          )}
        >
          <div className="flex items-start justify-between gap-2">
            {celdaTipo(p)}
            <span className="num shrink-0 text-[13px]">{abierto(p)}</span>
          </div>
          <div className="text-[13px] font-medium [overflow-wrap:anywhere]">{p.tiendaTexto}</div>
          {referencia(p)}
          <p className="m-0 text-[13px] leading-[1.45] text-metas-texto-3 [overflow-wrap:anywhere]">{p.detalle}</p>
          <div className="text-xs text-metas-texto-2">Resuelve: {p.area}</div>
          {!p.resuelto && botonAccion(p, true)}
        </li>
      ))}
    </ul>
  );

  return (
    <div className="flex flex-col gap-5" data-testid="pendientes-tab">
      {cabecera}
      {error && (
        <div className="flex flex-wrap items-center gap-2 rounded-metas-control border border-metas-error bg-metas-error-fondo px-4 py-3 text-sm text-metas-error" role="alert">
          <span className="font-semibold">No se pudieron leer los pendientes: {error}</span>
          <button type="button" className={CLASE_BOTON} onClick={() => void cargar()}>
            Reintentar
          </button>
        </div>
      )}
      {tarjetas}
      <section className={cx(CLASE_PANEL, 'overflow-hidden')} aria-label="Lista de pendientes" aria-busy={cargando || undefined}>
        {barra}
        {chipTienda}
        {panelAfectados}
        {cargando && !datos && <p className="m-0 px-4 py-6 text-sm text-metas-texto-2">Cargando pendientes…</p>}
        {visibles.length > 0 && (esMovil ? tarjetasMovil : tabla)}
        {!cargando && vacio}
      </section>
    </div>
  );
};

export default PendientesTab;
